import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Mail, Users, Layers, ListChecks, Settings, LogOut, PenLine, BookOpen, MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useSyncUserLocale } from '@/hooks/useSyncUserLocale'
import { NotificationBell } from '@/components/NotificationBell'
import { FeedbackButton } from '@/components/feedback/FeedbackButton'
import { LanguageSelector } from '@/components/LanguageSelector'
import { LegalLinks } from '@/components/LegalLinks'

const navItems = [
  { to: '/', icon: LayoutDashboard, key: 'dashboard' },
  { to: '/sequences', icon: Mail, key: 'sequences' },
  { to: '/contacts', icon: Users, key: 'contacts' },
  { to: '/segments', icon: Layers, key: 'segments' },
  { to: '/enrollments', icon: ListChecks, key: 'enrollments' },
  { to: '/signatures', icon: PenLine, key: 'signatures' },
  { to: '/feedback', icon: MessageSquare, key: 'feedback' },
  { to: '/settings', icon: Settings, key: 'settings' },
  { to: '/docs', icon: BookOpen, key: 'docs' },
]

export function Layout() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { t } = useTranslation('common')
  useSyncUserLocale(user?.id)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6">
          <h1 className="text-xl font-bold text-gray-900">
            {t('appName')}
          </h1>
        </div>
        <nav className="px-4 flex-1">
          {navItems.map(({ to, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span>{t(`nav.${key}`)}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || user?.email || t('userFallback')}
              </p>
              {user?.name && (
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              )}
            </div>
            <div className="flex items-center ml-2">
              <FeedbackButton />
              <NotificationBell />
              <LanguageSelector />
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('signOut')}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <footer className="border-t border-gray-200 px-4 py-3">
          <LegalLinks className="text-xs text-gray-500" />
        </footer>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
